import { FlaskConical, History, RefreshCw, Square } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import type { TestingAccess, TextRunActions, TextRunVm } from '../model/types'
import { Share, StateBadge, TestingGate } from './TestingParts'

/**
 * Разбор замера поиска по тексту (RP-FB34): итог, срезы по видам запросов, чем
 * нашёлся товар, худшие запросы первыми, сравнение с прошлым и условия.
 */
export function TextRunScreen({ access, vm, actions }: { access: TestingAccess; vm: TextRunVm; actions: TextRunActions }) {
  const { comparison } = vm
  return (
    <TestingGate access={access}>
      {vm.title === null ? (
        <QueryState query={vm.query} emptyText="Замер не найден" onRetry={actions.reload} />
      ) : (
        <section aria-labelledby="platform-text-run-title" className="platform-testing">
          <header className="platform-resources__toolbar">
            <h2 id="platform-text-run-title">{vm.title}</h2>
            <div className="platform-testing__row">
              {vm.active && (
                <Button
                  variant="ghost"
                  icon={<Square size={16} aria-hidden="true" />}
                  disabled={vm.action.busy}
                  onClick={actions.cancel}
                  actionId="platform.testing-text-run.cancel"
                >
                  Отменить
                </Button>
              )}
              <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-text-run.reload">
                Обновить
              </Button>
            </div>
          </header>
          <p>
            {vm.state !== null && <StateBadge badge={vm.state} />} · {vm.progress}
          </p>
          {vm.error !== null && <p className="platform-testing__note">Причина: {vm.error}</p>}
          <ActionError error={vm.action.error} onReload={actions.reload} />
          <p className="platform-testing__note">Каталог замера — тестовый: сравнивайте замеры между собой.</p>

          {vm.scores.length > 0 && (
            <section aria-label="Итог">
              <h3>Итог по всем запросам</h3>
              <dl className="platform-testing__scores">
                {vm.scores.map((cell) => (
                  <div key={cell.label}>
                    <dt>{cell.label}</dt>
                    <dd>
                      <Share share={cell.share} />
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {comparison !== null && (
            <section aria-label="Сравнение с прошлым замером">
              <header className="platform-resources__toolbar">
                <h3>Сравнение с прошлым замером</h3>
                <Button
                  variant="ghost"
                  icon={<History size={16} aria-hidden="true" />}
                  onClick={() => actions.openRun(comparison.previousRunId)}
                  actionId="platform.testing-text-run.open-previous"
                >
                  Открыть прошлый
                </Button>
              </header>
              {comparison.note !== null && <p className="platform-testing__note">{comparison.note}</p>}
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Метрика</th>
                      <th>Было</th>
                      <th>Стало</th>
                      <th>Разница</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.rows.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{row.previous}</td>
                        <td>{row.current}</td>
                        <td>{row.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {vm.kinds.length > 0 && (
            <section aria-label="По видам запросов">
              <h3>По видам запросов</h3>
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Вид</th>
                      <th>Первое место</th>
                      <th>В пятёрке</th>
                      <th>Не найден</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.kinds.map((kind) => (
                      <tr key={kind.kind}>
                        <td>
                          {kind.title}
                          <div className="platform-testing__note">запросов {kind.queries}</div>
                        </td>
                        <td>
                          <Share share={kind.top1} />
                        </td>
                        <td>
                          <Share share={kind.top5} />
                        </td>
                        <td>
                          <Share share={kind.notFound} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {vm.matches.length > 0 && (
            <section aria-label="Чем нашёлся товар">
              <h3>Чем нашёлся нужный товар</h3>
              <dl className="platform-testing__scores">
                {vm.matches.map((match) => (
                  <div key={match.key}>
                    <dt>{match.title}</dt>
                    <dd>
                      <strong>{match.queries}</strong> <small>запросов</small>
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {vm.worst.length > 0 && (
            <section aria-label="Запросы, худшие первыми">
              <h3>Запросы — худшие первыми</h3>
              <div className="platform-sync__table-wrap">
                <table className="platform-sync__table">
                  <thead>
                    <tr>
                      <th>Запрос</th>
                      <th>Должен найтись</th>
                      <th>Место</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.worst.map((row) => (
                      <tr key={row.queryId}>
                        <td>
                          <code>{row.text}</code>
                          <div className="platform-testing__note">{row.kindTitle}</div>
                        </td>
                        <td>{row.productName}</td>
                        <td>
                          <StateBadge badge={row.place} />
                          {row.detail === '' ? null : <div className="platform-testing__note">{row.detail}</div>}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            icon={<FlaskConical size={16} aria-hidden="true" />}
                            aria-label={`Проверить «${row.text}»`}
                            onClick={() => actions.check(row.queryId)}
                            actionId="platform.testing-text-run.check"
                          >
                            Проверить
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section aria-label="Время и условия">
            <h3>Время и условия</h3>
            <dl>
              <dt>Время ответа</dt>
              <dd>{vm.latency ?? 'нет данных'}</dd>
              <dt>Запросы со сбоем</dt>
              <dd>{vm.errors === 0 ? 'нет' : `${vm.errors} — в баллы не входят`}</dd>
              {vm.stamp.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </section>
      )}
    </TestingGate>
  )
}
